/**
 * modelDownloaderClient
 *
 * Purpose
 * - Provide a capped Web Worker pool for model downloads.
 * - Workers are managed by DownloadManager, which handles scheduling and priority.
 * - This module only manages the worker pool lifecycle (create/destroy workers).
 *
 * Notes
 * - The Service Worker will still intercept fetches initiated by workers.
 * - DownloadManager handles all scheduling, priority, and concurrency control.
 */
import type { DownloadOptions, DownloadProgress, DownloadResult } from '@/types/DownloadManager/modelDownloader';
import { proxy } from 'comlink';

type WorkerModule = typeof import('./ModelDownloadWorker');

export interface PoolWorker {
  instance: any; // Comlink worker instance
  busy: boolean;
  id: number;
}

let poolSize = 2;
let nextWorkerId = 1;
const workers: PoolWorker[] = [];

function createWorker(): PoolWorker {
  const instance = new ComlinkWorker<WorkerModule>(
    new URL('./ModelDownloadWorker', import.meta.url),
    { name: 'ModelDownloadWorker', type: 'module' }
  );
  const w: PoolWorker = { instance, busy: false, id: nextWorkerId++ };
  return w;
}

function ensurePoolSize() {
  // Grow
  while (workers.length < poolSize) {
    workers.push(createWorker());
  }
  // Shrink (terminate extras)
  while (workers.length > poolSize) {
    const w = workers.pop();
    try {
      // @ts-ignore - terminate is provided by vite-plugin-comlink worker wrapper
      w?.instance[Symbol.dispose]?.() ?? w?.instance?.terminate?.();
    } catch {
      // ignore
    }
  }
}

/**
 * Sets the worker pool size. Workers will be created/destroyed as needed.
 */
export function setDownloadWorkerPoolSize(n: number): void {
  const size = Math.max(1, Math.floor(n));
  poolSize = size;
}

/**
 * Gets an available worker from the pool, or null if all are busy.
 * Immediately marks the worker as busy to prevent race conditions.
 * DownloadManager should use this to directly assign tasks to workers.
 */
export function getAvailableWorker(): PoolWorker | null {
  ensurePoolSize();
  const worker = workers.find(w => !w.busy) || null;
  if (worker) {
    // Mark busy immediately to prevent race conditions in async scheduling
    worker.busy = true;
  }
  return worker;
}

/**
 * Gets all workers in the pool (for diagnostics).
 */
export function getAllWorkers(): PoolWorker[] {
  ensurePoolSize();
  return [...workers];
}

/**
 * Executes a download task on a specific worker.
 * DownloadManager should call this after getting an available worker via getAvailableWorker().
 * 
 * @param worker The worker to execute the task on (must be from getAvailableWorker())
 * @param modelId The model ID to download
 * @param options Download options
 * @param onProgress Progress callback
 * @returns Promise that resolves when download completes
 */
export async function executeOnWorker(
  worker: PoolWorker,
  modelId: string,
  options?: DownloadOptions,
  onProgress?: (p: DownloadProgress) => void
): Promise<DownloadResult> {
  // Worker should already be marked busy by getAvailableWorker()
  // This check is now a safety assertion, not the primary guard
  if (!worker.busy) {
    console.warn(`[executeOnWorker] Worker ${worker.id} was not marked busy - marking now`);
    worker.busy = true;
  }

  const progress = onProgress ? proxy(onProgress) : undefined;
  const { onProgress: op, ...rest } = options || {};
  const finalProgress = onProgress || op;

  try {
    const result = await (worker.instance as any).downloadModel(modelId, rest, finalProgress ? proxy(finalProgress) : undefined);
    return result;
  } finally {
    worker.busy = false;
  }
}

